import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RoleEntity } from '@magimp/shared';
import { useArchiveRole, useRestoreRole } from '@magimp/shared-hooks';
import { ConfirmModal } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export interface RoleArchiveConfirmProps {
  role: RoleEntity;
  onClose: () => void;
}

export function RoleArchiveConfirm({ role, onClose }: RoleArchiveConfirmProps) {
  const { t } = useTranslation(['roles', 'common']);
  const archive = useArchiveRole(role.id);
  const showToast = useToastStore((s) => s.show);
  const [visible, setVisible] = useState(true);
  const confirmingRef = useRef(false);
  useEffect(() => {
    if (visible) return;
    if (confirmingRef.current) {
      if (archive.isPending) return;
      if (archive.isError) {
        confirmingRef.current = false;
        setVisible(true);
        return;
      }
      onClose();
      return;
    }
    onClose();
  }, [visible, archive.isPending, archive.isError, onClose]);

  if (!visible) return null;

  const onConfirm = () => {
    confirmingRef.current = true;
    archive.mutate(undefined, {
      onSuccess: () => showToast({ tone: 'success', text: t('roles:toasts.archived') }),
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };
  return (
    <ConfirmModal
      title={t('roles:confirm.archiveTitle')}
      text={t('roles:confirm.archiveText', { name: role.displayName })}
      confirmLabel={t('common:actions.archive')}
      cancelLabel={t('common:actions.cancel')}
      danger
      onConfirm={onConfirm}
      onClose={() => setVisible(false)}
    />
  );
}

export interface RoleRestoreConfirmProps {
  role: RoleEntity;
  onClose: () => void;
}

export function RoleRestoreConfirm({ role, onClose }: RoleRestoreConfirmProps) {
  const { t } = useTranslation(['roles', 'common']);
  const restore = useRestoreRole(role.id);
  const showToast = useToastStore((s) => s.show);
  const [visible, setVisible] = useState(true);
  const confirmingRef = useRef(false);
  useEffect(() => {
    if (visible) return;
    if (confirmingRef.current) {
      if (restore.isPending) return;
      if (restore.isError) {
        confirmingRef.current = false;
        setVisible(true);
        return;
      }
      onClose();
      return;
    }
    onClose();
  }, [visible, restore.isPending, restore.isError, onClose]);

  if (!visible) return null;

  const onConfirm = () => {
    confirmingRef.current = true;
    restore.mutate(undefined, {
      onSuccess: () => showToast({ tone: 'success', text: t('roles:toasts.restored') }),
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };
  return (
    <ConfirmModal
      title={t('roles:confirm.restoreTitle')}
      text={t('roles:confirm.restoreText', { name: role.displayName })}
      confirmLabel={t('common:actions.restore')}
      cancelLabel={t('common:actions.cancel')}
      onConfirm={onConfirm}
      onClose={() => setVisible(false)}
    />
  );
}
